/**
 * Pure, client-safe: what the area page's browser clock (AreaRankClock) needs to judge ranked rows
 * again after an ISR page outlives a spot's local midnight, and the rule it applies. Kept out of
 * the "use client" module so the server view model can build the payload.
 */
import { rankAreaRows, type AreaRow } from "@/lib/areas/row-model";
import { boardEntryStillCurrentAt, type BoardEntryFreshnessFields } from "@/lib/conditions/board-freshness";

/** A ranked row's fields for re-ranking on the browser's clock. */
export type AreaClockRow = Readonly<Pick<AreaRow, "slug" | "status" | "score10" | "displayName" | "safetyState" | "rank">>;

export type AreaClockSport = Readonly<{
  /** The rows the server ranked, in its order. */
  rows: readonly AreaClockRow[];
  /** By slug, each ranked row's carried row stamp; a row without one keeps the server's verdict. */
  stamps: Readonly<Record<string, BoardEntryFreshnessFields>>;
}>;

export type AreaJudgedRanks = ReadonlyMap<string, Readonly<{ rank: number | null; current: boolean }>>;

/** Pure: the ranked rows a board hands the clock. */
export function areaClockRows(rows: readonly AreaRow[]): readonly AreaClockRow[] {
  return rows
    .filter((row) => row.status === "ranked")
    .map((row) => ({ slug: row.slug, status: row.status, score10: row.score10, displayName: row.displayName, safetyState: row.safetyState, rank: row.rank }));
}

/** Pure: one sport's judged ranks at `nowMs`, or null while every row is still current. */
export function judgeAreaClock(sport: AreaClockSport, nowMs: number): AreaJudgedRanks | null {
  const withdrawn = new Set(sport.rows.filter((row) => !boardEntryStillCurrentAt(sport.stamps[row.slug], nowMs)).map((row) => row.slug));
  if (withdrawn.size === 0) return null;
  const reranked = rankAreaRows(sport.rows.map((row) => (withdrawn.has(row.slug) ? { ...row, status: "stale" as const } : row)));
  return new Map(reranked.map((row) => [row.slug, { rank: row.rank, current: !withdrawn.has(row.slug) }]));
}

